/**
 * ═══════════════════════════════════════════════════════════════════════
 *  SVG Animasyon Motoru — Sıfır Bağımlılık (Web Animations API + CSS)
 *  Ders slaytlarındaki SVG görsellerine senkronize pedagojik animasyon uygular.
 * ═══════════════════════════════════════════════════════════════════════
 *
 *  Katman 1 & 2: Web Animations API — Pozisyon tabanlı sıralı giriş
 *    (Sol Kart → Bağlantı/Ok → Sağ Kart → Detaylar)
 *  Katman 3: SVG Çizgi Çizim (Line Drawing) — stroke-dasharray/dashoffset
 *  Sürekli Animasyon (Pulse): Sadece CSS (.svg-anim-pulse) ile çakışmasız
 *
 *  Erişilebilirlik: prefers-reduced-motion otomatik algılanır.
 *  Performans: GPU-accelerated will-change optimizasyonu.
 */

// ── Ayarlar ────────────────────────────────────────────────────────────────
const STAGGER_DELAY = 50;        // Elemanlar arası gecikme (ms)
const ELEMENT_DURATION = 380;    // Kart / metin giriş süresi (ms)
const LINE_DRAW_DURATION = 750;  // Çizgi / ok çizim süresi (ms)
const MAX_TOTAL_ANIM_MS = 2800;  // Toplam animasyon tavanı (ms)

// ── Erişilebilirlik — prefers-reduced-motion ───────────────────────────
function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return true;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ── Çizgi uzunluğu hesaplama ───────────────────────────────────────────
function getPathLength(el: SVGElement): number {
  try {
    if ('getTotalLength' in el && typeof (el as SVGGeometryElement).getTotalLength === 'function') {
      const len = (el as SVGGeometryElement).getTotalLength();
      if (len > 0) return len;
    }
  } catch { /* ignore */ }

  // <line> elemanları için Pisagor ile uzunluk
  if (el.tagName.toLowerCase() === 'line') {
    const x1 = parseFloat(el.getAttribute('x1') || '0');
    const y1 = parseFloat(el.getAttribute('y1') || '0');
    const x2 = parseFloat(el.getAttribute('x2') || '0');
    const y2 = parseFloat(el.getAttribute('y2') || '0');
    const dist = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    if (dist > 0) return dist;
  }

  // Fallback: BBox hipotenüsü
  const bbox = getBBox(el);
  if (bbox && (bbox.width > 0 || bbox.height > 0)) {
    return Math.sqrt(bbox.width ** 2 + bbox.height ** 2);
  }

  return 200;
}

// ── Güvenilir Bounding Box (DOM + Attributes + getBoundingClientRect) ────
function getBBox(el: SVGElement): { x: number; y: number; width: number; height: number } | null {
  try {
    const tag = el.tagName.toLowerCase();

    // 1. Tag-spesifik temel öznitelikler
    if (tag === 'rect') {
      return {
        x: parseFloat(el.getAttribute('x') || '0'),
        y: parseFloat(el.getAttribute('y') || '0'),
        width: parseFloat(el.getAttribute('width') || '0'),
        height: parseFloat(el.getAttribute('height') || '0'),
      };
    }

    if (tag === 'circle') {
      const cx = parseFloat(el.getAttribute('cx') || '0');
      const cy = parseFloat(el.getAttribute('cy') || '0');
      const r = parseFloat(el.getAttribute('r') || '0');
      return { x: cx - r, y: cy - r, width: r * 2, height: r * 2 };
    }

    if (tag === 'ellipse') {
      const cx = parseFloat(el.getAttribute('cx') || '0');
      const cy = parseFloat(el.getAttribute('cy') || '0');
      const rx = parseFloat(el.getAttribute('rx') || '0');
      const ry = parseFloat(el.getAttribute('ry') || '0');
      return { x: cx - rx, y: cy - ry, width: rx * 2, height: ry * 2 };
    }

    if (tag === 'line') {
      const x1 = parseFloat(el.getAttribute('x1') || '0');
      const y1 = parseFloat(el.getAttribute('y1') || '0');
      const x2 = parseFloat(el.getAttribute('x2') || '0');
      const y2 = parseFloat(el.getAttribute('y2') || '0');
      return {
        x: Math.min(x1, x2),
        y: Math.min(y1, y2),
        width: Math.abs(x2 - x1),
        height: Math.abs(y2 - y1),
      };
    }

    // 2. SVG DOM getBBox metodu (Next Tick sonrası tam çalışır)
    if ('getBBox' in el && typeof (el as SVGGraphicsElement).getBBox === 'function') {
      const box = (el as SVGGraphicsElement).getBBox();
      if (box && (box.width > 0 || box.height > 0 || box.x !== 0 || box.y !== 0)) {
        return { x: box.x, y: box.y, width: box.width, height: box.height };
      }
    }

    // 3. getBoundingClientRect (SVG kök koordinatlarına normalize edilmiş fallback)
    if (typeof el.getBoundingClientRect === 'function' && el.ownerSVGElement) {
      const elRect = el.getBoundingClientRect();
      const svgRect = el.ownerSVGElement.getBoundingClientRect();
      const viewBox = el.ownerSVGElement.viewBox?.baseVal;
      const scaleX = viewBox && viewBox.width > 0 && svgRect.width > 0 ? viewBox.width / svgRect.width : 1;
      const scaleY = viewBox && viewBox.height > 0 && svgRect.height > 0 ? viewBox.height / svgRect.height : 1;
      return {
        x: (elRect.left - svgRect.left) * scaleX,
        y: (elRect.top - svgRect.top) * scaleY,
        width: elRect.width * scaleX,
        height: elRect.height * scaleY,
      };
    }

    // Text fallback
    if (tag === 'text') {
      const x = parseFloat(el.getAttribute('x') || '0');
      const y = parseFloat(el.getAttribute('y') || '0');
      return { x, y: y - 14, width: 80, height: 20 };
    }
  } catch { /* ignore */ }
  return null;
}

// ── Elemanları pedagojik / pozisyonel gruplara ayır (Layer 2) ───────────
interface AnimGroup {
  label: 'background' | 'header' | 'left-card' | 'connector' | 'right-card' | 'general';
  elements: SVGElement[];
}

function classifyElements(svgEl: SVGSVGElement): AnimGroup[] {
  const allChildren = Array.from(svgEl.querySelectorAll<SVGElement>(
    'rect, circle, ellipse, polygon, path, line, polyline, text, g'
  ));

  const viewBoxWidth = svgEl.viewBox?.baseVal?.width || 560;
  const midX = viewBoxWidth / 2;

  const groups: Record<AnimGroup['label'], SVGElement[]> = {
    background: [],
    header: [],
    'left-card': [],
    connector: [],
    'right-card': [],
    general: [],
  };

  for (const el of allChildren) {
    // Sadece yaprak SVG elemanlarını al, <g> kapsayıcılarını atla
    if (el.tagName.toLowerCase() === 'g') continue;

    // Tam tuval arka plan rect'i
    if (el.tagName.toLowerCase() === 'rect') {
      const w = parseFloat(el.getAttribute('width') || '0');
      const h = parseFloat(el.getAttribute('height') || '0');
      if (w >= viewBoxWidth - 10 && h >= 240) {
        groups.background.push(el);
        continue;
      }
    }

    const bbox = getBBox(el);
    if (!bbox) {
      groups.general.push(el);
      continue;
    }

    const centerX = bbox.x + bbox.width / 2;
    const centerY = bbox.y + bbox.height / 2;

    // 1. Üst başlık alanı (y < 58)
    if (centerY < 58) {
      groups.header.push(el);
      continue;
    }

    // 2. Ortadaki bağlantı elemanı (ok, artı işareti, çizgi)
    const isConnector = (
      Math.abs(centerX - midX) < 55 &&
      (bbox.width < 110 || el.tagName.toLowerCase() === 'path' || el.tagName.toLowerCase() === 'line')
    );
    if (isConnector) {
      groups.connector.push(el);
      continue;
    }

    // 3. Sol kart vs Sağ kart ayrımı
    if (centerX < midX - 25) {
      groups['left-card'].push(el);
    } else if (centerX > midX + 25) {
      groups['right-card'].push(el);
    } else {
      groups.general.push(el);
    }
  }

  // Pedagojik sunum sırası:
  // Arka plan -> Başlık -> Sol Kart (Harf 1) -> Bağlantı/Ok (+) -> Sağ Kart (Sonuç/Hece) -> Diğerleri
  const orderedLabels: AnimGroup['label'][] = [
    'background', 'header', 'left-card', 'connector', 'right-card', 'general'
  ];

  return orderedLabels
    .filter(label => groups[label].length > 0)
    .map(label => ({ label, elements: groups[label] }));
}

// ── Ana Animasyon Fonksiyonu ───────────────────────────────────────────
export function animateSvg(container: HTMLElement | null): (() => void) | undefined {
  if (!container || prefersReducedMotion()) return undefined;

  const svgEl = container.querySelector<SVGSVGElement>('svg');
  if (!svgEl) return undefined;

  // Aktif animasyon ve zamanlayıcı takibi
  const activeAnimations: Animation[] = [];
  const activeTimers: number[] = [];

  // Container'a hazırlık sınıfını ekle
  container.classList.add('svg-anim-ready');

  const groups = classifyElements(svgEl);
  let globalDelay = 0;

  for (const group of groups) {
    // Arka plan hemen görünür olsun (animasyonsuz canvas tabanı)
    if (group.label === 'background') {
      for (const el of group.elements) {
        (el as HTMLElement).style.opacity = '1';
      }
      continue;
    }

    for (let i = 0; i < group.elements.length; i++) {
      const el = group.elements[i];
      const tag = el.tagName.toLowerCase();
      const delay = Math.min(globalDelay + i * STAGGER_DELAY, MAX_TOTAL_ANIM_MS);

      // ── Katman 3: SVG Çizgi Çizim (Path, Line, Polyline) ─────────
      if (tag === 'path' || tag === 'line' || tag === 'polyline') {
        const strokeAttr = el.getAttribute('stroke');
        const hasStroke = strokeAttr && strokeAttr !== 'none' && strokeAttr !== 'transparent';
        const fillAttr = el.getAttribute('fill');
        const hasFill = fillAttr && fillAttr !== 'none' && fillAttr !== 'transparent';

        if (hasStroke || tag === 'line') {
          const length = Math.max(getPathLength(el), 12);
          el.style.strokeDasharray = `${length}`;
          el.style.strokeDashoffset = `${length}`;

          // fill: 'both' ile bekleme süresince çizgi kesinlikle görünmez (opacity: 0) kalır
          const anim = el.animate(
            [
              { strokeDashoffset: `${length}`, opacity: 0 },
              { strokeDashoffset: `${length}`, opacity: 1, offset: 0.04 },
              { strokeDashoffset: '0', opacity: 1, offset: 1 },
            ],
            {
              duration: LINE_DRAW_DURATION,
              delay,
              easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
              fill: 'both',
            }
          );
          anim.onfinish = () => {
            el.style.opacity = '1';
            el.style.strokeDashoffset = '0';
          };
          activeAnimations.push(anim);

          // Dolgulu path/ok ise çizgi tamamlanmaya yakın dolguyu aç
          if (hasFill) {
            const origFill = fillAttr || '';
            el.setAttribute('fill', 'transparent');
            const fillAnim = el.animate(
              [
                { fill: 'transparent' },
                { fill: origFill },
              ],
              {
                duration: 300,
                delay: delay + LINE_DRAW_DURATION * 0.65,
                easing: 'ease-out',
                fill: 'both',
              }
            );
            fillAnim.onfinish = () => {
              el.setAttribute('fill', origFill);
            };
            activeAnimations.push(fillAnim);
          }
          continue;
        }
      }

      // ── Katman 2: Bağlantı Elemanlarına (Connector) Özel Zoom-in ────
      if (group.label === 'connector') {
        el.style.transformOrigin = 'center center';
        el.style.transformBox = 'fill-box';

        const connAnim = el.animate(
          [
            { opacity: 0, transform: 'scale(0.3)' },
            { opacity: 1, transform: 'scale(1.12)', offset: 0.7 },
            { opacity: 1, transform: 'scale(1)', offset: 1 },
          ],
          {
            duration: ELEMENT_DURATION + 100,
            delay,
            easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
            fill: 'both',
          }
        );
        connAnim.onfinish = () => {
          el.style.opacity = '1';
          el.style.transform = 'scale(1)';
        };
        activeAnimations.push(connAnim);
        continue;
      }

      // ── Katman 1 & 2: Fade-in + Slide-up (Şekil ve Metinler) ──────
      const isText = tag === 'text' || tag === 'tspan';
      const slideDistance = isText ? 8 : 12;

      const anim = el.animate(
        [
          { opacity: 0, transform: `translateY(${slideDistance}px)` },
          { opacity: 1, transform: 'translateY(0px)' },
        ],
        {
          duration: ELEMENT_DURATION,
          delay,
          easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
          fill: 'both',
        }
      );
      anim.onfinish = () => {
        el.style.opacity = '1';
        el.style.transform = 'translateY(0px)';
      };
      activeAnimations.push(anim);

      // ── Sürekli Animasyonlar (Pulse): Sadece CSS ile Yönetilir ──────
      // JS sadece giriş animasyonu bittikten sonra CSS sınıfını ekler.
      // Böylece Web Animations API ile CSS kuralları asla çakışmaz.
      if (isText) {
        const fontSize = parseFloat(el.getAttribute('font-size') || '12');
        const fontWeight = el.getAttribute('font-weight') || '';
        const isHeading = fontSize >= 13 || fontWeight === 'bold' || fontWeight === '700' || fontWeight === '800';

        if (isHeading) {
          const pulseTimer = window.setTimeout(() => {
            el.classList.add('svg-anim-pulse');
          }, delay + ELEMENT_DURATION + 120);
          activeTimers.push(pulseTimer);
        }
      }
    }

    // Grup arası örtüşmeli pedagojik gecikme
    const groupDuration = group.elements.length * STAGGER_DELAY + ELEMENT_DURATION;
    globalDelay += Math.min(groupDuration * 0.55, 500);
  }

  // Cleanup fonksiyonu: slayt değiştiğinde veya unmount anında tüm kaynakları serbest bırak
  return () => {
    for (const timer of activeTimers) {
      window.clearTimeout(timer);
    }
    activeTimers.length = 0;

    for (const anim of activeAnimations) {
      try { anim.cancel(); } catch { /* ignore */ }
    }
    activeAnimations.length = 0;

    cleanupSvgAnimations(container);
  };
}

// ── SVG Eleman Stillerini Sıfırla ──────────────────────────────────────
export function cleanupSvgAnimations(container: HTMLElement | null): void {
  if (!container) return;
  container.classList.remove('svg-anim-ready');

  const svgEl = container.querySelector<SVGSVGElement>('svg');
  if (!svgEl) return;

  const allEls = svgEl.querySelectorAll<SVGElement>(
    'rect, circle, ellipse, polygon, path, line, polyline, text, tspan, g'
  );
  for (const el of allEls) {
    el.classList.remove('svg-anim-pulse');
    el.style.opacity = '';
    el.style.strokeDasharray = '';
    el.style.strokeDashoffset = '';
    el.style.transform = '';
    el.style.transformOrigin = '';
    el.style.transformBox = '';
    if (typeof el.getAnimations === 'function') {
      el.getAnimations().forEach(a => { try { a.cancel(); } catch { /* ignore */ } });
    }
  }
}
